import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IMsalService } from '@azure/msal-angular';
import { AuthenticationResult, Logger, IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { buildConfiguration } from '@azure/msal-browser/dist/config/Configuration';
import { Observable, of } from 'rxjs';
import { b2cPolicies, protectedResourceScopes } from 'src/app/auth-config';
import { environment } from 'src/environments/environment';
import jwt_decode from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { ArkikStatusService } from './arkik-status.service';

interface IOfflineJWTPayload {
  iss: string;
  given_name: string;
  family_name: string;
  extension_CompanyName: string;
  emails: string[];
  nbf: number;
  exp: number;
  iat: number;
}

@Injectable()
export class MsalLocalService implements IMsalService {
  private authenticationResult: AuthenticationResult;
  private accountInfo: AccountInfo;
  instance: IPublicClientApplication;
  private setInstance(): void {
    this.instance = {
      acquireTokenByCode: async () => Promise.resolve(this.authenticationResult),
      acquireTokenPopup: async () => Promise.resolve(this.authenticationResult),
      acquireTokenRedirect: async () => Promise.resolve(),
      acquireTokenSilent: async () => Promise.resolve(this.authenticationResult),
      addEventCallback: () => '',
      addPerformanceCallback: () => '',
      disableAccountStorageEvents: () => undefined,
      enableAccountStorageEvents: () => undefined,
      getAccountByHomeId: () => this.accountInfo,
      getAccountByLocalId: () => this.accountInfo,
      getAccountByUsername: () => this.accountInfo,
      getActiveAccount: () => this.accountInfo,
      getAllAccounts: () => [this.accountInfo],
      handleRedirectPromise: async () => Promise.resolve(this.authenticationResult),
      initialize: async () => Promise.resolve(),
      initializeWrapperLibrary: () => undefined,
      loginPopup: async () => Promise.resolve(this.authenticationResult),
      loginRedirect: async () => Promise.resolve(),
      logout: async () => Promise.resolve(),
      logoutPopup: async () => Promise.resolve(),
      logoutRedirect: async () => Promise.resolve(),
      removeEventCallback: () => undefined,
      removePerformanceCallback: () => true,
      setLogger: () => undefined,
      setNavigationClient: () => undefined,
      setActiveAccount: () => undefined,
      ssoSilent: async () => Promise.resolve(this.authenticationResult),
      getLogger: () => new Logger({}),
      getTokenCache: () => {
        return {
          loadExternalTokens: () => this.authenticationResult,
        };
      },
      getConfiguration: () =>
        buildConfiguration(
          {
            auth: {
              clientId: environment.B2C_clientId,
            },
          },
          false,
        ),
    };
  }
  private setAuthResult(jwt: string, payload: IOfflineJWTPayload): void {
    this.authenticationResult = {
      accessToken: jwt,
      account: {
        environment: `${environment.B2C_tenantName}.b2clogin.com`,
        homeAccountId: '',
        localAccountId: '',
        tenantId: payload.extension_CompanyName,
        username: payload.emails?.length > 0 ? payload.emails[0] : '',
      },
      authority: b2cPolicies.authorities.signIn.authority,
      correlationId: '',
      expiresOn: new Date(payload.exp),
      fromCache: false,
      idToken: jwt,
      idTokenClaims: payload,
      scopes: protectedResourceScopes,
      tenantId: payload.extension_CompanyName,
      tokenType: 'JWT',
      uniqueId: uuidv4(),
    };
  }
  private setAccountInfo(jwt: string, payload: IOfflineJWTPayload): void {
    this.accountInfo = {
      environment: `${environment.B2C_tenantName}.b2clogin.com`,
      homeAccountId: '',
      localAccountId: '',
      tenantId: environment.B2C_tenantName,
      username: payload.emails?.length > 0 ? payload.emails[0] : '',
      name: payload.emails?.length > 0 ? payload.emails[0] : '',
      idToken: jwt,
      idTokenClaims: {
        ...payload,
      },
      nativeAccountId: '',
    };
  }
  constructor(
    private readonly _router: Router,
    private readonly _arkikStatusService: ArkikStatusService,
  ) {
    this.decodeToken();
  }

  private decodeToken(): void {
    let sessionToken = this._arkikStatusService.getOfflineToken();
    let keyProperty = 'key';

    if (!sessionToken || sessionToken.trim() === '') {
      // Offline token not found, try to get one generated online
      const keys = Object.keys(localStorage);
      const tokenKey: string = keys.find((x) =>
        x.includes(environment.B2C_tenantName + '.b2clogin.com' + '-accesstoken'),
      );
      sessionToken = localStorage.getItem(tokenKey);
      keyProperty = 'secret';
    }

    const tokenObj: { [key: string]: string } = JSON.parse(sessionToken);
    const jwt = tokenObj[keyProperty];
    const decodedToken = jwt_decode<IOfflineJWTPayload>(jwt);

    this.setAuthResult(jwt, decodedToken);
    this.setAccountInfo(jwt, decodedToken);
    this.setInstance();
  }

  initialize(): Observable<void> {
    return of();
  }
  acquireTokenPopup(): Observable<AuthenticationResult> {
    return of(this.authenticationResult);
  }
  acquireTokenRedirect(): Observable<void> {
    return of();
  }
  acquireTokenSilent(): Observable<AuthenticationResult> {
    return of(this.authenticationResult);
  }
  handleRedirectObservable(): Observable<AuthenticationResult | null> {
    return of(this.authenticationResult);
  }
  loginPopup(): Observable<AuthenticationResult> {
    return of(this.authenticationResult);
  }
  loginRedirect(): Observable<void> {
    return of();
  }
  logout(): Observable<void> {
    if (!this._arkikStatusService.isArkikOnline()) this._arkikStatusService.setSessionClosed();
    this._router.navigate(['/logout-offline']);
    return of();
  }
  logoutRedirect(): Observable<void> {
    return this.logout();
  }
  logoutPopup(): Observable<void> {
    return this.logout();
  }
  ssoSilent(): Observable<AuthenticationResult> {
    return of(this.authenticationResult);
  }
  getLogger(): Logger {
    return new Logger({});
  }
  setLogger(): void {
    return undefined;
  }
}
